import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/constants';
import { toast } from 'sonner';
import './EmployeeFormPage.css';

const EmployeeFormPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = searchParams.get('location') || 'krakow';

  // Form state
  const [formData, setFormData] = useState({
    // 1. Dane osobowe
    firstName: '',
    lastName: '',
    birthDate: '',
    citizenship: 'Polska',
    desiredPosition: '',
    contractUntil: '',
    
    // 2. Adres zamieszkania
    street: '',
    postalCode: '',
    city: '',
    country: 'Polska',
    
    // 3. Dane kontaktowe
    phone: '',
    email: '',
    
    // 4. Dane do wypłaty
    bankAccount: '',
    
    // 5. Dane podatkowe
    taxOffice: '',
    isFirstJob: false,
    
    // 6. Status zatrudnienia/nauki
    isStudent: false,
    schoolName: '',
    fieldOfStudy: '',
    studentIdValidUntil: '',
    
    // 7. Dane dodatkowe
    emergencyContact: '',
    emergencyPhone: '',
    
    // 8. Oświadczenia (11 checkboxów wymaganych)
    informedAboutTerms: false,
    knowsContractForm: false,
    informedAboutSalary: false,
    knowsScheduleRules: false,
    commitsToRealization: false,
    knowsLocalRules: false,
    commitsToPropertyCare: false,
    infoAccurate: false,
    dataProcessingConsent: false,
    noObjections: false,
    hadQuestionOpportunity: false
  });

  const [studentAttachment, setStudentAttachment] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.pdf')) {
      toast.error('Tylko pliki PDF są akceptowane');
      return;
    }

    // Validate file size (100MB)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 100) {
      toast.error(`Plik jest za duży (${fileSizeMB.toFixed(1)}MB). Maksymalny rozmiar to 100MB`);
      return;
    }

    setStudentAttachment(file);
    
    // Upload immediately
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('location', location);

      const response = await axios.post(
        `${API}/forms/upload-student-attachment`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setAttachmentUrl(response.data.url);
      toast.success(`Plik przesłany (${response.data.size_mb}MB)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd podczas przesyłania pliku');
      setStudentAttachment(null);
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    // Required fields
    if (!formData.firstName || !formData.lastName) {
      toast.error('Imię i nazwisko są wymagane');
      return false;
    }

    if (!formData.desiredPosition) {
      toast.error('Podaj stanowisko');
      return false;
    }

    if (!formData.contractUntil) {
      toast.error('Podaj datę do kiedy umowa');
      return false;
    }

    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Podaj prawidłowy adres email');
      return false;
    }

    if (!formData.phone) {
      toast.error('Numer telefonu jest wymagany');
      return false;
    }

    if (!formData.bankAccount) {
      toast.error('Numer konta bankowego jest wymagany');
      return false;
    }

    // Sprawdź wszystkie oświadczenia (11 checkboxów)
    const requiredDeclarations = [
      formData.informedAboutTerms,
      formData.knowsContractForm,
      formData.informedAboutSalary,
      formData.knowsScheduleRules,
      formData.commitsToRealization,
      formData.knowsLocalRules,
      formData.commitsToPropertyCare,
      formData.infoAccurate,
      formData.dataProcessingConsent,
      formData.noObjections,
      formData.hadQuestionOpportunity
    ];
    
    const missingDeclarations = requiredDeclarations.filter(d => !d);
    if (missingDeclarations.length > 0) {
      toast.error(`Musisz zaakceptować wszystkie oświadczenia (brakuje ${missingDeclarations.length})`);
      console.log('Missing declarations:', {
        informedAboutTerms: formData.informedAboutTerms,
        knowsContractForm: formData.knowsContractForm,
        informedAboutSalary: formData.informedAboutSalary,
        knowsScheduleRules: formData.knowsScheduleRules,
        commitsToRealization: formData.commitsToRealization,
        knowsLocalRules: formData.knowsLocalRules,
        commitsToPropertyCare: formData.commitsToPropertyCare,
        infoAccurate: formData.infoAccurate,
        dataProcessingConsent: formData.dataProcessingConsent,
        noObjections: formData.noObjections,
        hadQuestionOpportunity: formData.hadQuestionOpportunity
      });
      return false;
    }

    // Student fields - OPCJONALNE (nie wymagane)
    // Jeśli zaznaczył że jest studentem, pola są opcjonalne

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== FORM SUBMIT ATTEMPT ===');
    console.log('Form data:', formData);
    console.log('All declarations:', {
      informedAboutTerms: formData.informedAboutTerms,
      knowsContractForm: formData.knowsContractForm,
      informedAboutSalary: formData.informedAboutSalary,
      knowsScheduleRules: formData.knowsScheduleRules,
      commitsToRealization: formData.commitsToRealization,
      knowsLocalRules: formData.knowsLocalRules,
      commitsToPropertyCare: formData.commitsToPropertyCare,
      infoAccurate: formData.infoAccurate,
      dataProcessingConsent: formData.dataProcessingConsent,
      noObjections: formData.noObjections,
      hadQuestionOpportunity: formData.hadQuestionOpportunity
    });

    if (!validateForm()) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('✅ Validation passed, submitting...');
    setSubmitting(true);

    try {
      const payload = {
        location: location,
        personalData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          birthDate: formData.birthDate,
          citizenship: formData.citizenship,
          desiredPosition: formData.desiredPosition,
          contractUntil: formData.contractUntil
        },
        address: {
          street: formData.street,
          postalCode: formData.postalCode,
          city: formData.city,
          country: formData.country
        },
        contact: {
          phone: formData.phone,
          email: formData.email
        },
        payment: {
          bankAccount: formData.bankAccount
        },
        taxData: {
          taxOffice: formData.taxOffice,
          isFirstJob: formData.isFirstJob
        },
        studentStatus: {
          isStudent: formData.isStudent,
          schoolName: formData.schoolName || null,
          fieldOfStudy: formData.fieldOfStudy || null,
          studentIdValidUntil: formData.studentIdValidUntil || null,
          attachmentUrl: attachmentUrl || null
        },
        additional: {
          emergencyContact: formData.emergencyContact || null,
          emergencyPhone: formData.emergencyPhone || null
        },
        declarations: {
          informedAboutTerms: formData.informedAboutTerms,
          knowsContractForm: formData.knowsContractForm,
          informedAboutSalary: formData.informedAboutSalary,
          knowsScheduleRules: formData.knowsScheduleRules,
          commitsToRealization: formData.commitsToRealization,
          knowsLocalRules: formData.knowsLocalRules,
          commitsToPropertyCare: formData.commitsToPropertyCare,
          infoAccurate: formData.infoAccurate,
          dataProcessingConsent: formData.dataProcessingConsent,
          noObjections: formData.noObjections,
          hadQuestionOpportunity: formData.hadQuestionOpportunity
        }
      };

      await axios.post(`${API}/forms/employee`, payload);

      toast.success('✅ Formularz wysłany pomyślnie!');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error submitting form:', error);
      
      let errorMessage = 'Błąd podczas wysyłania formularza';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Obsługa błędów Pydantic (array of validation errors)
        if (typeof detail === 'object' && Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            const field = err.loc?.join('.') || 'pole';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(13,27,42,0.95) 0%, rgba(27,38,59,0.92) 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '40px',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            color: '#fff',
            margin: '0 0 8px 0'
          }}>
            📋 Formularz Pracownika
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px' }}>
            Lokal: <span style={{ color: 'var(--gold)', fontWeight: '600' }}>
              {location === 'krakow' ? '🏰 Kraków' : '🏔️ Nowy Sącz'}
            </span>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* 1. Dane osobowe */}
          <FormSection title="1. Dane osobowe" icon="👤">
            <div className="form-grid">
              <FormField label="Imię *" required>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Jan"
                />
              </FormField>

              <FormField label="Nazwisko *" required>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Kowalski"
                />
              </FormField>

              <FormField label="Data urodzenia *" required>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                />
              </FormField>

              <FormField label="Obywatelstwo *" required>
                <input
                  type="text"
                  name="citizenship"
                  value={formData.citizenship}
                  onChange={handleChange}
                  required
                  placeholder="Polska"
                />
              </FormField>

              <FormField label="Jakie stanowisko *" required>
                <input
                  type="text"
                  name="desiredPosition"
                  value={formData.desiredPosition}
                  onChange={handleChange}
                  required
                  placeholder="Kelner, Kucharz, Barman..."
                />
              </FormField>

              <FormField label="Do kiedy umowa *" required>
                <input
                  type="date"
                  name="contractUntil"
                  value={formData.contractUntil}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </div>
          </FormSection>

          {/* 2. Adres zamieszkania */}
          <FormSection title="2. Adres zamieszkania" icon="🏠">
            <div className="form-grid">
              <FormField label="Ulica i numer domu/lokalu *" required fullWidth>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  required
                  placeholder="ul. Przykładowa 123/45"
                />
              </FormField>

              <FormField label="Kod pocztowy *" required>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="33-300"
                />
              </FormField>

              <FormField label="Miejscowość *" required>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="Nowy Sącz"
                />
              </FormField>

              <FormField label="Kraj *" required fullWidth>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder="Polska"
                />
              </FormField>
            </div>
          </FormSection>

          {/* 3. Dane kontaktowe */}
          <FormSection title="3. Dane kontaktowe" icon="📞">
            <div className="form-grid">
              <FormField label="Numer telefonu *" required>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+48 123 456 789"
                />
              </FormField>

              <FormField label="Adres e-mail *" required>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="jan.kowalski@example.com"
                />
              </FormField>
            </div>
          </FormSection>

          {/* 4. Dane do wypłaty wynagrodzenia */}
          <FormSection title="4. Dane do wypłaty wynagrodzenia" icon="💰">
            <FormField label="Numer rachunku bankowego *" required fullWidth>
              <input
                type="text"
                name="bankAccount"
                value={formData.bankAccount}
                onChange={handleChange}
                required
                placeholder="PL 1234 5678 9012 3456 7890 1234"
              />
            </FormField>
          </FormSection>

          {/* 5. Dane podatkowe i urzędowe */}
          <FormSection title="5. Dane podatkowe i urzędowe" icon="🏛️">
            <FormField label="Właściwy Urząd Skarbowy *" required fullWidth>
              <input
                type="text"
                name="taxOffice"
                value={formData.taxOffice}
                onChange={handleChange}
                required
                placeholder="Urząd Skarbowy Nowy Sącz"
              />
            </FormField>

            <div style={{ marginTop: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '15px'
              }}>
                <input
                  type="checkbox"
                  name="isFirstJob"
                  checked={formData.isFirstJob}
                  onChange={handleChange}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span>Czy jest to Twoja pierwsza praca w życiu?</span>
              </label>
            </div>
          </FormSection>

          {/* 6. Status zatrudnienia/nauki */}
          <FormSection title="6. Status zatrudnienia / nauki" icon="🎓">
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                color: '#fff',
                fontSize: '15px'
              }}>
                <input
                  type="checkbox"
                  name="isStudent"
                  checked={formData.isStudent}
                  onChange={handleChange}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span>Czy posiadasz status ucznia lub studenta?</span>
              </label>
            </div>

            {formData.isStudent && (
              <div style={{
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '16px'
              }}>
                <div className="form-grid">
                  <FormField label="Nazwa szkoły / uczelni (opcjonalne)">
                    <input
                      type="text"
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      placeholder="AGH Kraków"
                    />
                  </FormField>

                  <FormField label="Kierunek / typ szkoły (opcjonalne)">
                    <input
                      type="text"
                      name="fieldOfStudy"
                      value={formData.fieldOfStudy}
                      onChange={handleChange}
                      placeholder="Informatyka"
                    />
                  </FormField>

                  <FormField label="Data ważności legitymacji (opcjonalne)">
                    <input
                      type="date"
                      name="studentIdValidUntil"
                      value={formData.studentIdValidUntil}
                      onChange={handleChange}
                    />
                  </FormField>

                  <FormField label="Załącznik - legitymacja (PDF, max 100MB) - opcjonalne" fullWidth>
                    <div style={{
                      border: '2px dashed rgba(212, 175, 55, 0.5)',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.03)'
                    }}>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        disabled={uploading}
                        style={{ display: 'none' }}
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        style={{
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          color: 'var(--gold)',
                          fontSize: '14px'
                        }}
                      >
                        {uploading ? (
                          '⏳ Przesyłanie...'
                        ) : attachmentUrl ? (
                          <span>
                            ✅ Plik przesłany: {studentAttachment?.name}
                            <br />
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                              Kliknij aby zmienić
                            </span>
                          </span>
                        ) : (
                          <span>
                            📎 Kliknij aby wybrać plik PDF
                            <br />
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                              Maksymalny rozmiar: 100MB
                            </span>
                          </span>
                        )}
                      </label>
                    </div>
                  </FormField>
                </div>
              </div>
            )}
          </FormSection>

          {/* 7. Dane dodatkowe (opcjonalne) */}
          <FormSection title="7. Dane dodatkowe (opcjonalne)" icon="➕">
            <div className="form-grid">
              <FormField label="Osoba do kontaktu w nagłym wypadku">
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  placeholder="Imię i nazwisko"
                />
              </FormField>

              <FormField label="Numer telefonu kontaktu awaryjnego">
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleChange}
                  placeholder="+48 123 456 789"
                />
              </FormField>
            </div>
          </FormSection>

          {/* 8. Oświadczenia */}
          <FormSection title="8. Oświadczenia" icon="✅">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input 
                  type="checkbox" 
                  name="informedAboutTerms" 
                  checked={formData.informedAboutTerms} 
                  onChange={handleChange} 
                  style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} 
                />
                <span>Oświadczam, że zostałem/am poinformowany/a o warunkach współpracy, zakresie czynności oraz zasadach wykonywania zlecenia i są one dla mnie zrozumiałe</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="knowsContractForm" checked={formData.knowsContractForm} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Oświadczam, że znana jest mi forma współpracy (umowa zlecenie) oraz wynikające z niej prawa i obowiązki</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="informedAboutSalary" checked={formData.informedAboutSalary} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Oświadczam, że zostałem/am poinformowany/a o stawce wynagrodzenia oraz zasadach jego naliczania i wypłaty i akceptuję te warunki</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="knowsScheduleRules" checked={formData.knowsScheduleRules} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Oświadczam, że znane są mi zasady ustalania terminów wykonywania zlecenia oraz moja deklarowana dyspozycyjność</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="commitsToRealization" checked={formData.commitsToRealization} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Zobowiązuję się do realizacji przyjętych zleceń zgodnie z ustaleniami</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="knowsLocalRules" checked={formData.knowsLocalRules} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Oświadczam, że zapoznałem/am się z zasadami obowiązującymi w lokalu, w tym standardami obsługi gości, i zobowiązuję się do ich przestrzegania</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="commitsToPropertyCare" checked={formData.commitsToPropertyCare} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Zobowiązuję się do dbania o powierzone mienie (sprzęt, kasa, terminal, klucze itp.)</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="infoAccurate" checked={formData.infoAccurate} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Oświadczam, że podane przeze mnie informacje, w tym dotyczące statusu ucznia/studenta, są aktualne oraz zobowiązuję się do niezwłocznego poinformowania o ich zmianie</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="dataProcessingConsent" checked={formData.dataProcessingConsent} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Wyrażam zgodę na przetwarzanie moich danych osobowych w celu realizacji umowy zlecenia</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="noObjections" checked={formData.noObjections} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Potwierdzam, że wszystkie warunki współpracy są dla mnie jasne i nie mam do nich zastrzeżeń</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#fff', fontSize: '14px', lineHeight: '1.6' }}>
                <input type="checkbox" name="hadQuestionOpportunity" checked={formData.hadQuestionOpportunity} onChange={handleChange} style={{ width: '20px', height: '20px', cursor: 'pointer', marginTop: '2px' }} />
                <span>Potwierdzam, że przed rozpoczęciem współpracy miałem/am możliwość zadania pytań i uzyskania wyjaśnień dotyczących warunków zlecenia</span>
              </label>
            </div>
          </FormSection>

          {/* Submit buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginTop: '32px'
          }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.5 : 1
              }}
            >
              ← Anuluj
            </button>

            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 2,
                padding: '16px',
                background: submitting
                  ? 'rgba(156, 163, 175, 0.5)'
                  : 'linear-gradient(135deg, rgba(212, 175, 55, 0.3) 0%, rgba(212, 175, 55, 0.15) 100%)',
                border: '1px solid rgba(212, 175, 55, 0.4)',
                borderRadius: '12px',
                color: submitting ? 'rgba(255,255,255,0.5)' : 'var(--gold)',
                fontSize: '16px',
                fontWeight: '700',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? '⏳ Wysyłanie...' : '📤 Wyślij formularz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper components
const FormSection = ({ title, icon, children }) => (
  <div style={{
    marginBottom: '32px',
    padding: '24px',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  }}>
    <h2 style={{
      fontSize: '20px',
      fontWeight: '700',
      color: '#fff',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <span style={{ fontSize: '24px' }}>{icon}</span>
      {title}
    </h2>
    {children}
  </div>
);

const FormField = ({ label, required, fullWidth, children }) => (
  <div style={{
    gridColumn: fullWidth ? 'span 2' : 'span 1',
    marginBottom: '4px'
  }}>
    <label style={{
      display: 'block',
      color: 'rgba(255,255,255,0.9)',
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px'
    }}>
      {label}
      {required && <span style={{ color: 'var(--gold)', marginLeft: '4px' }}>*</span>}
    </label>
    <div style={{
      '& input, & textarea, & select': {
        width: '100%',
        padding: '12px',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '8px',
        color: '#fff',
        fontSize: '15px',
        outline: 'none'
      }
    }}>
      {children}
    </div>
  </div>
);

export default EmployeeFormPage;
